import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import loadingAnimation from "../assets/Loading.json"; // Adjust path as needed
import octopusAnimation from "../assets/Coming.json"; // Added Lottie animation import

interface HealthProps {
  userId: string;
  userName: string;
  onBack: () => void;
  ageYears: number;
  ageDays: number;
  theme?: string; // Optional theme prop to align with ThinkyPlayProfile
}

interface HealthQuestion {
  id: number;
  text: string;
  options: string[];
  interval: string;
}

interface HealthCard {
  date: string;
  age: string;
  type: "basic" | "special" | "vaccine";
  questions?: HealthQuestion[];
  milestone?: string;
}

const Health: React.FC<HealthProps> = ({ userId, userName, onBack, ageYears, ageDays, theme = "black" }) => {
  const navigate = useNavigate();
  const [currentCardIndex, setCurrentCardIndex] = useState<number>(0);
  const [healthCards, setHealthCards] = useState<HealthCard[]>([]);
  const [answers, setAnswers] = useState<{ [key: string]: { [key: number]: string } }>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  // Temporary flag to control "coming soon" message
  const isComingSoon = true; // Set to false when the feature is ready

  const basicQuestions: HealthQuestion[] = [
    { id: 1, text: "Did you drink enough water today?", options: ["Yes", "No"], interval: "08:00 AM" },
    { id: 2, text: "Did you eat a balanced breakfast?", options: ["Yes", "No"], interval: "09:00 AM" },
    { id: 3, text: "Did you get at least 30 minutes of physical activity?", options: ["Yes", "No"], interval: "02:00 PM" },
    { id: 4, text: "Did you sleep for at least 8 hours last night?", options: ["Yes", "No"], interval: "08:00 PM" },
    { id: 5, text: "Did you brush your teeth twice today?", options: ["Yes", "No"], interval: "09:00 PM" },
  ];

  const specialQuestions: HealthQuestion[] = [
    { id: 1, text: "Do you feel stressed or anxious today?", options: ["Not at all", "A little", "A lot"], interval: "08:00 AM" },
    { id: 2, text: "Have you spent time with friends or family?", options: ["Yes", "No"], interval: "10:00 AM" },
    { id: 3, text: "Did you try a new healthy food today?", options: ["Yes", "No"], interval: "01:00 PM" },
    { id: 4, text: "Have you felt any unusual pain or discomfort?", options: ["Yes", "No"], interval: "06:00 PM" },
    { id: 5, text: "Did you take any vitamins or supplements?", options: ["Yes", "No"], interval: "07:00 PM" },
  ];

  const vaccineMilestones: { [key: number]: string[] } = {
    1: ["Hepatitis B (2nd dose)"],
    2: ["DTaP (1st dose)", "Polio (1st dose)"],
    4: ["MMR (1st dose)", "Varicella (1st dose)"],
    6: ["Hepatitis A (1st dose)"],
    12: ["HPV (1st dose)", "Meningococcal (1st dose)"],
    15: ["Tdap Booster"],
    18: ["Meningococcal Booster"],
  };

  useEffect(() => {
    // Skip card generation if in "coming soon" mode
    if (isComingSoon) {
      setLoading(false);
      return;
    }

    // console.log("Health component props:", { userId, userName, ageYears, ageDays }); // For debugging
    const generateHealthCards = () => {
      // Validate ageDays
      if (!Number.isFinite(ageDays) || ageDays <= 0) {
        setError("Invalid or zero age data provided. Please update your profile with a valid date of birth.");
        setLoading(false);
        return;
      }

      const cards: HealthCard[] = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Normalize to midnight
      const birthDate = new Date(today.getTime() - ageDays * 24 * 60 * 60 * 1000); // Calculate birth date

      // Check if birthDate is valid
      if (isNaN(birthDate.getTime())) {
        setError("Invalid birth date calculated. Please check your date of birth.");
        setLoading(false);
        return;
      }

      // Handle leap year birthdays (use March 1 for non-leap years if born on Feb 29)
      const isLeapDay = birthDate.getMonth() === 1 && birthDate.getDate() === 29;
      const isLeapYear = (year: number) => (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);

      // Generate cards for a 30-day range
      const maxCards = 30;
      for (let i = 0; i < maxCards; i++) {
        let currentDate = new Date(birthDate.getTime() + i * 24 * 60 * 60 * 1000);

        // Adjust for Feb 29 birthdays in non-leap years
        if (isLeapDay && !isLeapYear(currentDate.getFullYear())) {
          currentDate = new Date(currentDate.getFullYear(), 2, 1); // Use March 1
        }

        if (isNaN(currentDate.getTime())) {
          console.warn(`Invalid date at index ${i}`);
          continue;
        }

        const dateStr = currentDate.toISOString().split("T")[0];
        const years = Math.floor((ageDays + i) / 365);
        const days = (ageDays + i) % 365;
        const ageStr = `${years} year${years !== 1 ? "s" : ""} ${days} day${days !== 1 ? "s" : ""}`;
        let card: HealthCard;

        if (i % 7 === 2 && vaccineMilestones[years]) {
          card = {
            date: dateStr,
            age: ageStr,
            type: "vaccine",
            milestone: vaccineMilestones[years][0] || "General Health Checkup",
          };
        } else if (i % 7 === 1) {
          card = {
            date: dateStr,
            age: ageStr,
            type: "special",
            questions: specialQuestions,
          };
        } else {
          card = {
            date: dateStr,
            age: ageStr,
            type: "basic",
            questions: basicQuestions,
          };
        }
        cards.push(card);
      }

      if (cards.length === 0) {
        setError("No valid health cards could be generated. Please check your date of birth.");
        setLoading(false);
        return;
      }

      setHealthCards(cards);
      setLoading(false);
    };

    generateHealthCards();
  }, [ageDays]);

  const handleAnswer = async (date: string, questionId: number, answer: string) => {
    setAnswers((prev) => ({
      ...prev,
      [date]: { ...prev[date], [questionId]: answer },
    }));

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/health/answers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId, date, questionId, answer }),
      });

      if (!response.ok) {
        console.error("Failed to save health answer:", await response.text());
      }
    } catch (err) {
      console.error("Error saving health answer:", err);
    }
  };

  const handleSwipe = (direction: "left" | "right") => {
    if (direction === "right" && currentCardIndex < healthCards.length - 1) {
      setCurrentCardIndex((prev) => prev + 1);
    } else if (direction === "left" && currentCardIndex > 0) {
      setCurrentCardIndex((prev) => prev - 1);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[var(--background-color)]"
        aria-label="Loading health data"
      >
        <Lottie
          animationData={loadingAnimation}
          loop={true}
          className="loading-animation"
        />
      </div>
    );
  }

  if (isComingSoon) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center bg-[var(--background-color)] px-4 coming-soon-container"
        aria-label="Feature coming soon"
      >
        <Lottie
          animationData={octopusAnimation}
          loop={true}
          className="coming-soon-animation"
        />
        <p className="text-cyan font-bold text-center coming-soon-text">
          Will be available in the upcoming Update!
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-cosmic-pink text-center min-h-screen flex items-center justify-center px-4">
        {error}
      </div>
    );
  }

  const currentCard = healthCards[currentCardIndex] || {};

  return (
    <div className={`health-container theme-${theme}`}>
      <style>
        {`
          :root {
            --cyber-green: #a8fd2d;
            --electric-cyan: #22d3ee;
            --cosmic-pink: #ff1493;
            --midnight-black: #000000;
            --glass-bg: rgba(10, 14, 43, 0.3);
            --card-blur: 12px;
            --background-color: var(--midnight-black);
            --text-color: white;
            --card-border-color: rgba(255, 255, 255, 0.1);
            --card-text-color: white;
            --card-bg: #000000;
            --input-bg: #1a202c;
            --input-text: white;
            --input-border: #4a5568;
            --gray-text: #e5e7eb;
            --gray-text-secondary: #9ca3af;
          }

          .theme-white {
            --background-color: #f5f5f5;
            --text-color: #333;
            --card-border-color: #d1d5db;
            --card-text-color: #333;
            --card-bg: rgba(255, 255, 255, 0.8);
            --glass-bg: rgba(255, 255, 255, 0.3);
            --input-bg: #ffffff;
            --input-text: #333;
            --input-border: #d1d5db;
            --gray-text: #4b5563;
            --gray-text-secondary: #6b7280;
          }

          .theme-pink {
            --background-color: #fff0f6;
            --text-color: #333;
            --card-border-color: #f9a8d4;
            --card-text-color: #333;
            --card-bg: rgba(255, 240, 246, 0.8);
            --glass-bg: rgba(255, 240, 246, 0.3);
akawa            --input-bg: #ffffff;
            --input-text: #333;
            --input-border: #f9a8d4;
            --gray-text: #4b5563;
            --gray-text-secondary: #6b7280;
          }

          .health-container {
            max-width: 1000px;
            margin: 120px auto 0;
            padding: 16px;
            min-height: calc(100vh - 120px);
            background: var(--background-color);
            color: var(--text-color);
            display: flex;
            flex-direction: column;
            gap: 16px;
            box-sizing: border-box;
          }

          .health-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px;
            background: var(--glass-bg);
            backdrop-filter: blur(var(--card-blur));
            border: 1px solid var(--card-border-color);
            border-radius: 15px;
          }

          .user-info {
            display: flex;
            align-items: center;
            gap: 12px;
          }

          .user-icon {
            width: 48px;
            height: 48px;
            background: linear-gradient(45deg, var(--electric-cyan), var(--cyber-green));
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
            color: white;
          }

          .back-button,
          .logout-button {
            background: var(--glass-bg);
            border: 1px solid var(--card-border-color);
            border-radius: 8px;
            padding: 8px 16px;
            color: var(--text-color);
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .back-button:hover,
          .logout-button:hover {
            border-color: var(--cyber-green);
            box-shadow: 0 0 10px var(--cyber-green);
          }

          .back-button:active,
          .logout-button:active {
            transform: scale(0.95);
          }

          .card-container {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 16px;
            padding: 16px;
            position: relative;
          }

          .health-card {
            background: var(--card-bg);
            border: 1px solid var(--card-border-color);
            border-radius: 15px;
            padding: 16px;
            width: 100%;
            max-width: 500px;
            transition: all 0.3s ease;
            position: relative;
            color: var(--card-text-color);
            box-shadow: 0 0 15px var(--cyber-green);
          }

          .health-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: var(--glass-bg);
            backdrop-filter: blur(var(--card-blur));
            z-index: -1;
            border-radius: 15px;
          }

          .health-card:hover {
            box-shadow: 0 0 20px var(--cyber-green);
            border-color: var(--cyber-green);
            transform: scale(1.02);
          }

          .swipe-button {
            background: var(--glass-bg);
            border: 1px solid var(--card-border-color);
            border-radius: 50%;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .swipe-button:hover {
            border-color: var(--cyber-green);
            box-shadow: 0 0 10px var(--cyber-green);
          }

          .swipe-button:active {
            transform: scale(0.95);
          }

          .question-container {
            margin-top: 16px;
          }

          .question {
            font-size: 16px;
            font-weight: 600;
            color: var(--text-color);
            margin-bottom: 8px;
          }

          .question-interval {
            font-size: 12px;
            color: var(--gray-text-secondary);
            margin-bottom: 12px;
          }

          .option-button {
            padding: 8px 16px;
            border-radius: 8px;
            border: 1px solid var(--card-border-color);
            background: var(--glass-bg);
            backdrop-filter: blur(var(--card-blur));
            color: var(--text-color);
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-right: 8px;
            margin-bottom: 8px;
          }

          .option-button:hover {
            border-color: var(--cyber-green);
            color: var(--cyber-green);
          }

          .option-button.selected {
            background: linear-gradient(45deg, var(--electric-cyan), var(--cyber-green));
            border-color: var(--cyber-green);
            color: white;
            box-shadow: 0 0 15px var(--cyber-green);
            transform: scale(1.05);
          }

          .theme-pink .option-button.selected {
            background: linear-gradient(45deg, #f9a8d4,rgb(0, 255, 255));
            border-color:rgb(0, 119, 255);
            box-shadow: 0 0 15px rgba(0, 255, 242, 0.7);
            color: white;
          }

          .theme-white .option-button.selected {
            background: linear-gradient(45deg, #d1d5db, #000000);
            border-color: #000000;
            box-shadow: 0 0 15px rgba(0, 0, 0, 0.7);
            color: white;
          }

          .milestone {
            font-size: 18px;
            font-weight: 600;
            color: var(--cyber-green);
            margin-top: 16px;
            text-align: center;
          }

          /* Styles for coming soon and loading sections */
          .coming-soon-container, .loading-container {
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
            overflow: hidden;
            padding: 16px;
          }

          .coming-soon-animation, .loading-animation {
            width: 30vw;
            height: 30vw;
            max-width: 100px;
            max-height: 100px;
            min-width: 100px;
            min-height: 100px;
          }

          .coming-soon-text {
            font-size: 5vw;
            max-width: 90%;
            line-height: 1.4;
            margin-top: 16px;
          }

          @media (max-width: 768px) {
            .health-container {
              padding: 12px;
              margin-top: 100px;
              min-height: calc(100vh - 100px);
            }

            .health-header {
              flex-direction: column;
              gap: 12px;
            }

            .back-button,
            .logout-button {
              padding: 6px 12px;
              font-size: 12px;
            }

            .health-card {
              padding: 12px;
            }

            .swipe-button {
              width: 32px;
              height: 32px;
            }

            .question {
              font-size: 14px;
            }

            .question-interval {
              font-size: 11px;
            }

            .option-button {
              padding: 6px 12px;
              font-size: 12px;
            }

            .milestone {
              font-size: 16px;
            }

            .user-icon {
              width: 40px;
              height: 40px;
              font-size: 18px;
            }

            /* Mobile-specific styles for coming soon and loading sections */
            .coming-soon-container, .loading-container {
              padding: 8px;
            }

            .coming-soon-animation, .loading-animation {
              width: 50vw;
              height: 50vw;
              max-width: 200px;
              max-height: 200px;
              min-width: 120px;
              min-height: 120px;
            }

            .coming-soon-text {
              font-size: 4.5vw;
              max-width: 95%;
              margin-top: 12px;
            }
          }

          @media (max-width: 480px) {
            .coming-soon-animation, .loading-animation {
              width: 45vw;
              height: 45vw;
              max-width: 180px;
              max-height: 180px;
              min-width: 100px;
              min-height: 100px;
            }

            .coming-soon-text {
              font-size: 4vw;
              margin-top: 10px;
            }
          }
        `}
      </style>

      <div className="health-container">
        <div className="health-header">
          <div className="user-info">
            <div className="user-icon">{userName[0]}</div>
            <div>
              <h2 className="text-xl font-bold">{userName}</h2>
              <p className="text-sm text-gray-text">Age: {ageYears} year{ageYears !== 1 ? "s" : ""}</p>
            </div>
          </div>
          <div>
            <button className="back-button" onClick={onBack}>
              Back to Profile
            </button>
            <button className="logout-button" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
        {healthCards.length > 0 ? (
          <div className="card-container">
            <button
              className="swipe-button"
              onClick={() => handleSwipe("left")}
              disabled={currentCardIndex === 0}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <div className="health-card">
              <h3 className="text-lg font-bold text-cyber-green">
                {currentCard.age} - {currentCard.type.charAt(0).toUpperCase() + currentCard.type.slice(1)}
              </h3>
              <p className="text-sm text-gray-text-secondary">{currentCard.date}</p>
              {currentCard.type === "vaccine" ? (
                <div className="milestone">{currentCard.milestone}</div>
              ) : (
                <div className="question-container">
                  {currentCard.questions?.map((question) => (
                    <div key={question.id}>
                      <p className="question">{question.text}</p>
                      <p className="question-interval">Answer at: {question.interval}</p>
                      <div>
                        {question.options.map((option) => (
                          <button
                            key={option}
                            className={`option-button ${
                              answers[currentCard.date]?.[question.id] === option ? "selected" : ""
                            }`}
                            onClick={() => handleAnswer(currentCard.date, question.id, option)}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              className="swipe-button"
              onClick={() => handleSwipe("right")}
              disabled={currentCardIndex === healthCards.length - 1}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>
        ) : (
          <div className="text-cosmic-pink text-center">No health cards available.</div>
        )}
      </div>
    </div>
  );
};

export default Health;